import React, { useState, useEffect } from 'react';

/**
 * EventTypeSelector - Event type selection
 * Displays teacher's available courses as selectable cards
 *
 * @param {number} teacherId - User ID of the teacher
 * @param {function} onEventTypeSelect - Callback when user selects an event type
 * @param {object} translations - Translation object for i18n
 */
const EventTypeSelector = ({ teacherId, onEventTypeSelect, translations = {} }) => {
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEventTypes();
  }, [teacherId]);

  const fetchEventTypes = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/teachers/${teacherId}/event_types`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load courses');
      }

      const data = await response.json();

      if (data.success) {
        setEventTypes(data.data);
      } else {
        throw new Error(data.error || 'Failed to load courses');
      }
    } catch (err) {
      console.error('Error fetching event types:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3366FF]"></div>
          <p className="text-sm text-gray-600">
            {translations.loading || 'Loading courses...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-lg font-medium text-red-900">
              {translations.error_title || 'Load failed'}
            </h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={fetchEventTypes}
              className="mt-3 text-sm font-medium text-red-600 hover:text-red-800 underline"
            >
              {translations.retry || 'Retry'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (eventTypes.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        <p className="mt-4 text-gray-600">
          {translations.no_courses || 'No courses available for booking'}
        </p>
      </div>
    );
  }

  // Main content - Event Type cards
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {translations.select_course_title || 'Select a Course'}
        </h2>
        <p className="text-gray-600">
          {translations.select_course_subtitle || 'Choose the type of course you want to book'}
        </p>
      </div>

      {/* Event Type Cards */}
      <div className="grid grid-cols-1 gap-4">
        {eventTypes.map((eventType) => (
          <button
            key={eventType.id}
            onClick={() => onEventTypeSelect(eventType)}
            className="group text-left border border-gray-200 rounded-lg p-6 hover:border-[#3366FF] hover:shadow-lg transition-all duration-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2"
          >
            <div className="flex items-start justify-between">
              {/* Left side - Course info */}
              <div className="flex-1 min-w-0">
                {/* Course title */}
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#3366FF] transition-colors mb-2">
                  {eventType.title}
                </h3>

                {/* Course meta info (duration & price) */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                  {/* Duration */}
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{eventType.duration_minutes} {translations.minutes || 'min'}</span>
                  </span>

                  {/* Price (if available) */}
                  {eventType.price && parseFloat(eventType.price) > 0 && (
                    <span className="flex items-center gap-1 font-medium text-[#3366FF]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        {eventType.currency === 'TWD' ? 'NT$' : eventType.currency} {parseFloat(eventType.price).toLocaleString()}
                      </span>
                    </span>
                  )}
                </div>

                {/* Course description */}
                {eventType.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {eventType.description}
                  </p>
                )}
              </div>

              {/* Right side - Arrow icon */}
              <svg
                className="w-6 h-6 text-gray-400 group-hover:text-[#3366FF] group-hover:translate-x-1 transition-all flex-shrink-0 ml-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default EventTypeSelector;
